import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkModeContext } from 'store/work-mode';
import MainLayout from 'components/main-layout/MainLayout';
import { FaRegCalendarAlt, FaPen } from 'react-icons/fa';
import styled from 'styled-components';
import useTrainingMaterials from './components/trainingMaterials';

interface VideoWithBannerProps {
  video: VideoDetails;
  index: number;
  currentLanguage: string;
}

interface VideoDetails {
  url: string;
  title: string;
  description: string;
}

const iconMapping = {
  FaCalendarPdf: FaRegCalendarAlt,
  FaPenPfd: FaPen,
};

const VideoWithBanner: FC<VideoWithBannerProps> = ({ video, index, currentLanguage }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <VideoWrapper isReversed={index % 2 !== 0}>
      <VideoContent>
        <VideoTitle>{video.title}</VideoTitle>
        <VideoDescription>{video.description}</VideoDescription>
      </VideoContent>
      <VideoContainer onClick={() => setIsPlaying(!isPlaying)}>
        {currentLanguage !== 'en' ? (
          <>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              src={`${video.url}`}
              width="560"
              height="315"
              title={video.title}
              poster="/assets/images/video-poster.png"
              preload="none"
              controls
              style={{ display: 'block' }}
            />
          </>
        ) : (
          <ComingSoon>Videos coming soon</ComingSoon>
        )}
      </VideoContainer>
    </VideoWrapper>
  );
};

const Screen: FC = () => {
  const trainingMaterials = useTrainingMaterials();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const { workMode } = useWorkModeContext();
  const mode = workMode === 'employer' ? 'employer' : 'employee';
  const materials = trainingMaterials[mode] || { pdfs: [], videos: [] };

  return (
    <MainLayout title={t('menu.trainingMaterials')}>
      <Container>
        <DocumentsSection>
          {materials.pdfs
            .filter(pdf => pdf.language === currentLanguage || pdf.language === 'both' || !pdf.language)
            .map((pdf: any) => (
              <PDFLink
                key={pdf.url}
                href={pdf.url}
                download
              >
                <PDFLinkWithIcon>
                  {iconMapping[pdf.icon as keyof typeof iconMapping] ? (
                    <IconWrapper>{React.createElement(iconMapping[pdf.icon as keyof typeof iconMapping])}</IconWrapper>
                  ) : (
                    <IconWrapper>
                      <img
                        src={pdf.icon}
                        alt="PDF icon"
                      />
                    </IconWrapper>
                  )}
                  {pdf.name}
                </PDFLinkWithIcon>
              </PDFLink>
            ))}
        </DocumentsSection>
        <VideoSection>
          {materials.videos.map((video, index) => (
            <VideoWithBanner
              key={video.url}
              video={video}
              index={index}
              currentLanguage={i18n.language}
            />
          ))}
        </VideoSection>
      </Container>
    </MainLayout>
  );
};

export default Screen;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const PDFLink = styled.a`
  color: #575962;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    color: #716aca;
    text-decoration: underline;
  }
`;

const PDFLinkWithIcon = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const IconWrapper = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 16px;
  height: 16px;

  & > img {
    width: 100%;
    height: 100%;
  }

  & > svg {
    width: 100%;
    height: 100%;
  }
`;

const DocumentsSection = styled.div`
  display: flex;
  gap: 20px;
  padding: 20px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
`;

const VideoSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 40px;
  align-items: center;
`;

const VideoWrapper = styled.div<{ isReversed: boolean }>`
  display: flex;
  flex-direction: ${({ isReversed }) => (isReversed ? 'row-reverse' : 'row')};
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  max-width: 1140px;

  @media (max-width: 991px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const VideoContent = styled.div`
  flex: 1;
  @media (max-width: 991px) {
    text-align: center;
  }
`;

const VideoTitle = styled.h3`
  // Styles if needed else remove
`;

const VideoDescription = styled.p`
  // Styles if needed else remove
`;

const VideoContainer = styled.div`
  position: relative;
  cursor: pointer;
  width: 560px;
  height: 315px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 630px) {
    width: 300px;
    height: 165px;

    & iframe {
      width: 300px;
      height: 165px;
    }
  }

  .loader-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.5);
    z-index: 1;
  }

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
  }
`;

const ComingSoon = styled.div`
  font-size: 24px;
  border: 1px solid #000;
  width: 560px;
  height: 315px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
`;
